import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, NotesSection, Note } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { usePermissions } from '../hooks/usePermissions';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { VendorOrdersTab } from '../components/vendors/VendorOrdersTab';
import { VendorAnalyticsTab } from '../components/vendors/VendorAnalyticsTab';

type VendorTab = 'overview' | 'products' | 'orders' | 'analytics';
const TAB_ICONS: Record<VendorTab, string> = { overview: 'information-circle-outline', products: 'cube-outline', orders: 'receipt-outline', analytics: 'stats-chart-outline' };

interface VendorDetailScreenProps {
  vendorId: string;
}

/** Mirrors VendorFormScreen's parseNotes — backend stores `notes` as a
 * single string that may hold a JSON-serialized array of authored entries. */
const parseNotes = (raw: any): Note[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((note: any) => (typeof note === 'string'
      ? { content: note, createdBy: { id: '', email: '' }, createdAt: new Date() }
      : { ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((note: any) => ({ ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
      }
    } catch {
      return [{ content: raw, createdBy: { id: '', email: '' }, createdAt: new Date() }];
    }
  }
  return [];
};

export const VendorDetailScreen: React.FC<VendorDetailScreenProps> = ({ vendorId }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { navigateTo, activeTab, setActiveTab, showNotification, routeParams } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageVendors = hasPermissionAtLeast('general.manageVendors', 'edit');
  // Orders hold customer data: the Orders / Analytics tabs need orders access.
  const canSeeOrders = hasPermissionAtLeast('general.manageOrders', 'view');
  const tabs: VendorTab[] = canSeeOrders ? ['overview', 'products', 'orders', 'analytics'] : ['overview', 'products'];
  const [tab, setTab] = useState<VendorTab>(() => (tabs.includes(routeParams?.tab) ? routeParams!.tab : 'overview'));
  // Threaded through from VendorsListScreen's scoped mode — passed along to
  // vendor-edit so its "Extra Fields" section (custom Data List columns)
  // knows which list to load/save against. Without this, editing a vendor
  // via its detail screen silently lost the scoped-list context and the
  // edit form never showed (or could save) any extra field.
  const listId: string | undefined = routeParams?.listId;

  const [vendor, setVendor] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadVendor = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getVendorById(vendorId);
      if (response.statusCode === 1000 && response.data) {
        setVendor(response.data);
      } else {
        showNotification('error', 'Error', 'Vendor not found');
        navigateTo('vendors');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  const loadProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const response = await apiService.getVendorProducts(vendorId, { limit: 50 });
      if (response.statusCode === 1000 && response.data) {
        setProducts(response.data.products || []);
      }
    } catch (error: any) {
      console.error('Error loading vendor products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadVendor();
    loadProducts();
  }, [vendorId]);

  const handleUnlinkProduct = async (productId: string) => {
    try {
      const response = await apiService.removeProductFromVendor(vendorId, productId);
      if (response.statusCode === 1000) {
        setProducts((prev) => prev.filter((p) => p._id !== productId));
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to unlink product');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to unlink product');
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await apiService.deleteVendor(vendorId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Deleted', 'Vendor deleted successfully');
        navigateTo('vendors');
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to delete vendor');
        setShowDeleteModal(false);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to delete vendor');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const { t } = useLocale();
  const infoRow = (icon: string, label: string, value?: string) => value ? (
    <View style={[styles.infoRow, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name={icon as any} size={18} color={colors.textLight} />
      <View style={styles.infoTextContainer}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  ) : null;

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={vendor?.name || 'Vendor'}
          onBack={() => navigateTo('vendors')}
          actions={canManageVendors ? [
            { icon: 'create-outline', onPress: () => navigateTo('vendor-edit', vendorId, listId ? { listId } : undefined), accessibilityLabel: 'Edit' },
            { icon: 'trash-outline', onPress: () => setShowDeleteModal(true), accessibilityLabel: 'Delete' },
          ] : []}
        />
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
            <View style={[styles.tabs, { flexDirection: rtl.rowDirection }]}>
              {tabs.map((k) => (
                <TouchableOpacity key={k} style={[styles.tab, tab === k && styles.tabActive, { flexDirection: rtl.rowDirection }]} onPress={() => setTab(k)} accessibilityRole="tab" accessibilityState={{ selected: tab === k }}>
                  <Ionicons name={TAB_ICONS[k] as any} size={16} color={(tab === k ? colors.primary : colors.textLight) as string} />
                  <Text style={[styles.tabText, tab === k && styles.tabTextActive]} numberOfLines={1}>{t(`vendors.tab.${k}` as any)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {tab === 'overview' && (
              <>
                <View style={styles.infoCard}>
                  {infoRow('call-outline', t('vendors.detail.phone'), vendor?.phone)}
                  {infoRow('mail-outline', t('vendors.detail.email'), vendor?.email)}
                  {infoRow('location-outline', t('vendors.detail.address'), vendor?.address || vendor?.location?.address)}
                  {infoRow('document-text-outline', t('vendors.detail.crNumber'), vendor?.crNumber)}
                  {infoRow('receipt-outline', t('vendors.detail.vatNumber'), vendor?.vatNumber)}
                  {infoRow('cart-outline', t('vendors.minimumOrder.amount'), (vendor as any)?.minimumOrderAmount ? `${(vendor as any).minimumOrderCurrency || ''} ${Number((vendor as any).minimumOrderAmount).toFixed(2)}`.trim() : undefined)}
                  {vendor?.notes && parseNotes(vendor.notes).length > 0 && (
                    <View style={styles.notesBlock}>
                      <NotesSection notes={parseNotes(vendor.notes)} onAddNote={() => {}} editable={false} title={t('vendors.detail.notes')} />
                    </View>
                  )}
                </View>

                {vendor?.attachments?.length > 0 && (
                  <>
                    <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('vendors.detail.files')}</Text>
                    {vendor.attachments.map((att: any) => (
                      <View key={att.url} style={[styles.attachmentRow, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="document-outline" size={18} color={colors.textLight} />
                        <Text style={styles.attachmentName} numberOfLines={1}>{att.name}</Text>
                      </View>
                    ))}
                  </>
                )}
              </>
            )}

            {tab === 'products' && (
              <>
                <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.sectionTitle, styles.sectionTitleTight, { textAlign: rtl.textStart }]}>{t('vendors.detail.productsSupplied', { count: products.length })}</Text>
                  {canManageVendors && (
                    <TouchableOpacity style={[styles.addButton, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('vendor-add-products', vendorId)}>
                      <Ionicons name="add" size={18} color={colors.primary} />
                      <Text style={styles.addButtonText}>{t('vendors.detail.manageProducts')}</Text>
                    </TouchableOpacity>
                  )}
                </View>

                {loadingProducts ? (
                  <ActivityIndicator size="small" color={colors.primary} style={{ marginTop: spacing.md }} />
                ) : products.length === 0 ? (
                  <Text style={styles.emptyText}>{t('vendors.detail.noProducts')}</Text>
                ) : (
                  products.map((p) => (
                    <View key={p._id} style={[styles.productRow, { flexDirection: rtl.rowDirection }]}>
                      <View style={styles.productInfo}>
                        <Text style={styles.productName} numberOfLines={1}>{p.name}</Text>
                        <Text style={styles.productPrice}>{p.currency} {p.price}</Text>
                      </View>
                      {canManageVendors && (
                        <TouchableOpacity onPress={() => handleUnlinkProduct(p._id)}>
                          <Ionicons name="close-circle-outline" size={20} color={colors.error} />
                        </TouchableOpacity>
                      )}
                    </View>
                  ))
                )}
              </>
            )}

            {tab === 'orders' && vendor && (
              <VendorOrdersTab vendor={vendor} products={products} onOpenOrder={(orderId) => navigateTo('order-edit', orderId)} />
            )}

            {tab === 'analytics' && <VendorAnalyticsTab vendorId={vendorId} />}
          </ScrollView>
        )}
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        title="Delete Vendor"
        message="Are you sure you want to delete this vendor? This can't be undone."
        confirmText="Delete"
        type="error"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  infoCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.sm,
  },
  infoRow: { alignItems: 'flex-start', gap: spacing.sm, paddingVertical: spacing.xs },
  infoTextContainer: { flex: 1 },
  infoLabel: { fontSize: typography.fontSize.xs, color: colors.textLight },
  infoValue: { fontSize: typography.fontSize.sm, color: colors.textDark, marginTop: 2 },
  notesBlock: { paddingTop: spacing.sm },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  sectionHeader: { alignItems: 'center', justifyContent: 'space-between' },
  sectionTitleTight: { marginTop: 0 },
  tabs: { gap: spacing.xs, marginBottom: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  tab: { alignItems: 'center', gap: 6, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderBottomWidth: 2, borderBottomColor: 'transparent', marginBottom: -1 },
  tabActive: { borderBottomColor: colors.primary },
  tabText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  tabTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  addButton: { alignItems: 'center', gap: spacing.xs },
  addButtonText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
  attachmentRow: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  attachmentName: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.textLight, marginTop: spacing.sm },
  productRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    marginBottom: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  productInfo: { flex: 1 },
  productName: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  productPrice: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
});
